/**
 * Cloth topology derivation — turns a PolygonTopology + its render-space
 * triangle indices into the typed-array buffers `settleParticles` needs for
 * a cloth simulation:
 *
 *   - `structuralEdges` — every unique mesh edge, in shared-vertex space.
 *     These become XPBD distance constraints; solving them preserves the
 *     cloth's resting shape under stretch.
 *
 *   - `bendingPairs` — for each edge shared by exactly two triangles, the
 *     two vertices opposite that edge across those triangles. Treated as
 *     a second set of distance constraints with their own compliance, this
 *     is the isometric-bending formulation (Bergou '06) — the simplest
 *     stable bending model for PBD cloth.
 *
 *   - `triangles` — the render triangles remapped from per-corner indices
 *     to shared-vertex indices. Used by particle–triangle self-collision
 *     and by bending-pair extraction. N-gons are already fan-triangulated
 *     by the time they reach `indices`, so we inherit that triangulation
 *     verbatim rather than re-partitioning.
 *
 * All outputs live in shared-vertex index space (the same space as
 * `SceneObject.vertices`), so they're drop-in inputs for the solver.
 */

import { buildSharedVertexIndices } from "./loopExpand.ts";
import type {
  PolygonTopology,
  RenderTriangleIndices,
  SharedTriangleIndices,
} from "./types.ts";

/** Upper bound on vertex count for the pair-key packing trick. 2^26 is well
 *  above any realistic PoseRef mesh (67M verts) and `26 + 26 = 52` bits
 *  stays inside IEEE-754 double integer precision (53 bits). */
const MAX_VERT_COUNT = 1 << 26;

export interface ClothTopology {
  /** Flat [a, b, a, b, ...] vertex-index pairs, one per mesh edge. */
  structuralEdges: Uint32Array;
  /** Flat [a, b, a, b, ...] vertex-index pairs; `a` and `b` are the two
   *  vertices opposite each other across a triangle edge shared by two
   *  triangles. Empty if every edge is boundary or non-manifold. */
  bendingPairs: Uint32Array;
  /** Flat [a, b, c, a, b, c, ...] triangle vertex-index triples. */
  triangles: SharedTriangleIndices;
}

/**
 * Extract cloth-simulation inputs from a PolygonTopology and its render
 * indices. `renderIndices` must be the triangle-expanded per-corner buffer
 * (i.e. `SceneObject.indices`); this function remaps it to shared-vertex
 * space internally.
 *
 * Edge-sharing lookup uses a pair-key hash to find triangle pairs in
 * O(triangleCount). Non-manifold edges (shared by 3+ triangles) skip
 * bending-pair generation rather than picking two arbitrary neighbours —
 * the result would be direction-dependent and visually unstable.
 */
export function buildClothTopology(
  topology: PolygonTopology,
  renderIndices: RenderTriangleIndices,
): ClothTopology {
  // Structural edges come straight from the authoritative topology buffer;
  // clone so downstream callers can't mutate the source.
  const structuralEdges = new Uint32Array(topology.edges);

  // Triangles in shared-vertex space, inherited from the renderer's fan
  // triangulation of any n-gons.
  const triangles = buildSharedVertexIndices(renderIndices, topology);
  const triCount = triangles.length / 3;

  // Edge-key → triangle index. Value semantics:
  //   undefined  — edge not yet seen
  //   >= 0       — edge seen by exactly one triangle; value is its index
  //   -1         — edge already paired (or non-manifold); skip further hits
  const edgeToTri = new Map<number, number>();
  const bendingPairs: number[] = [];

  for (let t = 0; t < triCount; t++) {
    const va = triangles[t * 3];
    const vb = triangles[t * 3 + 1];
    const vc = triangles[t * 3 + 2];

    processEdge(va, vb, vc, va, vb, t, triangles, edgeToTri, bendingPairs);
    processEdge(va, vb, vc, vb, vc, t, triangles, edgeToTri, bendingPairs);
    processEdge(va, vb, vc, vc, va, t, triangles, edgeToTri, bendingPairs);
  }

  return {
    structuralEdges,
    bendingPairs: Uint32Array.from(bendingPairs),
    triangles,
  };
}

/** Canonicalize (ea, eb) to (min, max) and process one triangle edge. */
function processEdge(
  va: number,
  vb: number,
  vc: number,
  ea: number,
  eb: number,
  triIdx: number,
  triangles: SharedTriangleIndices,
  edgeToTri: Map<number, number>,
  bendingPairs: number[],
): void {
  const lo = ea < eb ? ea : eb;
  const hi = ea < eb ? eb : ea;
  const key = lo * MAX_VERT_COUNT + hi;
  const prev = edgeToTri.get(key);

  if (prev === undefined) {
    edgeToTri.set(key, triIdx);
    return;
  }
  if (prev < 0) return; // non-manifold — already flagged

  // Second triangle sharing this edge. Emit the opposite-vertex pair.
  const pa = triangles[prev * 3];
  const pb = triangles[prev * 3 + 1];
  const pc = triangles[prev * 3 + 2];
  const oppPrev = oppositeVertex(pa, pb, pc, lo, hi);
  const oppCurr = oppositeVertex(va, vb, vc, lo, hi);
  if (oppPrev !== oppCurr) {
    bendingPairs.push(oppPrev, oppCurr);
  }
  edgeToTri.set(key, -1);
}

/** Return whichever of (va, vb, vc) is neither `ea` nor `eb`. Assumes the
 *  edge genuinely lies in the triangle — two of the three verts match. */
function oppositeVertex(
  va: number,
  vb: number,
  vc: number,
  ea: number,
  eb: number,
): number {
  if (va !== ea && va !== eb) return va;
  if (vb !== ea && vb !== eb) return vb;
  return vc;
}
